import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Car,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  Star,
  Clock,
  Users,
  Award,
  Phone,
  ChevronDown,
  FileText,
  MapPin,
} from 'lucide-react';

function Counter({ target, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        obs.disconnect();

        const s = performance.now();

        const tick = (now) => {
          const p = Math.min((now - s) / 1800, 1);
          setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.3 }
    );

    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div
      ref={ref}
      className="font-display font-black text-4xl md:text-5xl text-gray-950"
    >
      {val.toLocaleString('sv-SE')}
      {suffix}
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180 text-red-500' : ''
          }`}
        />
      </button>

      {open && (
        <div className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

const SERVICES = [
  {
    icon: <Car size={24} />,
    title: 'Körlektioner',
    desc: 'Professionell körkortsutbildning med certifierade instruktörer i Kista.',
    link: '/korlektioner',
    cta: 'Se paket',
  },
  {
    icon: <FileText size={24} />,
    title: 'Teorilektioner',
    desc: 'Gå igenom trafikregler, vägmärken och körkortsteorin med instruktör.',
    link: '/teorilektioner',
    cta: 'Boka teori',
  },
  {
    icon: <BookOpen size={24} />,
    title: 'Riskutbildning Del 1',
    desc: 'Obligatorisk teorikurs om mörker, trötthet, hastighet och alkohol.',
    link: '/riskutbildning-1',
    cta: 'Läs mer',
  },
  {
    icon: <AlertTriangle size={24} />,
    title: 'Riskutbildning Del 2',
    desc: 'Praktisk halkbana och riskmoment inför körkortet.',
    link: '/riskutbildning-2',
    cta: 'Läs mer',
  },
  {
    icon: <FileText size={24} />,
    title: 'Gratis Teoriprov',
    desc: 'Öva online med 65 frågor som liknar det riktiga teoriprovet.',
    link: '/teoriprov',
    cta: 'Prova nu',
  },
];

const PACKAGES = [
  { lektioner: 5, pris: 3750, popular: false },
  { lektioner: 10, pris: 7500, popular: true },
  { lektioner: 15, pris: 11250, popular: false },
  { lektioner: 20, pris: 15000, popular: false },
];

const FAQ = [
  {
    q: 'Hur många lektioner behöver jag?',
    a: 'De flesta behöver 20–40 lektioner. Vi rekommenderar att börja med ett paket och sedan utvärdera tillsammans med instruktören.',
  },
  {
    q: 'Är riskutbildningarna obligatoriska?',
    a: 'Ja. Riskutbildning Del 1 och Del 2 är obligatoriska moment för B-körkort i Sverige.',
  },
  {
    q: 'Kan jag boka kvällar och helger?',
    a: 'Vi erbjuder flexibla tider på vardagar och vissa helgtider beroende på tillgänglighet.',
  },
  {
    q: 'Hur bokar jag en lektion?',
    a: 'Du kan boka online via hemsidan eller ringa oss direkt.',
  },
  {
    q: 'Var ligger trafikskolan?',
    a: 'Alfa Trafikskola finns på Nordkapsgatan 8 Tr, 164 36 Kista.',
  },
  {
    q: 'Har ni teorilektioner?',
    a: 'Ja. Vi erbjuder teorilektioner och gratis teoriprov online.',
  },
];

export default function Home() {
  return (
    <>
      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white border-b border-gray-100">
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-red-100 blur-3xl opacity-60" />

        <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-7 shadow-sm">
            <MapPin size={13} className="text-red-500" />
            Kista, Stockholm
          </div>

          <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl text-gray-950 uppercase leading-[0.9] tracking-tight mb-7">
            Ta körkortet med
            <br />
            <span className="text-red-600">Alfa Trafikskola</span>
          </h1>

          <p className="text-gray-500 text-lg md:text-xl leading-relaxed mb-9 max-w-2xl mx-auto">
            Moderna körlektioner, tydlig handledning och flexibla tider i
            Kista. Boka online och kom igång med din körkortsresa.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-10">
            <Link to="/korlektioner" className="btn-red justify-center">
              <Car size={16} />
              Boka Körlektion
            </Link>

            <Link to="/teoriprov" className="btn-red-outline justify-center">
              <FileText size={16} />
              Gratis Teoriprov
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
            {[
              'Certifierade instruktörer',
              'Flexibla tider',
              'Boka online',
              'Personlig utbildning',
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-1.5 text-sm text-gray-500 font-medium"
              >
                <CheckCircle size={14} className="text-red-500 shrink-0" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
          {[
            { v: 750, s: '+', l: 'Nöjda Elever' },
            { v: 15, s: ' år', l: 'Erfarenhet' },
            { v: 94, s: '%', l: 'Godkänt Direkt' },
            { v: 2, s: '', l: 'Instruktörer' },
          ].map(({ v, s, l }) => (
            <div key={l} className="py-8 px-4 text-center">
              <Counter target={v} suffix={s} />
              <div className="text-gray-400 text-xs uppercase tracking-widest mt-1 font-semibold">
                {l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="section-tag mb-3">Våra Tjänster</div>
            <h2 className="font-display font-black text-4xl md:text-5xl text-gray-950 uppercase tracking-tight">
              Allt du behöver för körkortet
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto mt-3">
              Körlektioner, teori, riskutbildning och digitala verktyg samlat
              på ett ställe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-xl hover:shadow-gray-200/70 transition-all duration-200 group"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-800 mb-5 group-hover:bg-red-50 group-hover:text-red-600 transition-colors">
                  {s.icon}
                </div>

                <h3 className="font-bold text-gray-950 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                  {s.desc}
                </p>

                <Link
                  to={s.link}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 hover:text-red-600 transition-colors"
                >
                  {s.cta}
                  <ChevronRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PACKAGES ── */}
      <section className="py-20 bg-white" id="priser">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="section-tag mb-3">Priser</div>
            <h2 className="font-display font-black text-4xl md:text-5xl text-gray-950 uppercase tracking-tight">
              Lektionspaket
            </h2>
            <p className="text-gray-500 mt-2">
              Tydliga priser. Välj paket efter din nivå och erfarenhet.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PACKAGES.map((p) => (
              <div
                key={p.lektioner}
                className={`relative bg-white rounded-3xl p-6 text-center transition-all hover:-translate-y-1 hover:shadow-xl ${
                  p.popular
                    ? 'border-2 border-red-500 shadow-lg shadow-red-100'
                    : 'border border-gray-200 shadow-sm'
                }`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap">
                    Populärt
                  </div>
                )}

                <div className="font-display font-black text-6xl text-gray-950 leading-none">
                  {p.lektioner}
                </div>
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-4">
                  lektioner
                </div>

                <div className="font-display font-black text-2xl text-gray-950">
                  {p.pris.toLocaleString('sv-SE')}
                </div>
                <div className="text-sm text-gray-400 mb-5">kr · 750/lek</div>

                <Link
                  to="/korlektioner"
                  className={`text-xs font-bold uppercase tracking-wide py-2.5 px-4 rounded-xl transition-colors block ${
                    p.popular
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-100 text-gray-800 hover:bg-red-50 hover:text-red-600'
                  }`}
                >
                  Välj paket
                </Link>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            {[
              {
                name: 'Riskutbildning Del 1',
                pris: '499',
                note: 'Teori · 3 timmar',
                to: '/riskutbildning-1',
                icon: <BookOpen size={20} />,
              },
              {
                name: 'Riskutbildning Del 2',
                pris: '1 990',
                note: 'Halkbana · Heldag',
                to: '/riskutbildning-2',
                icon: <AlertTriangle size={20} />,
              },
              {
                name: 'Teorilektioner',
                pris: 'Fr. 400',
                note: 'Online & på plats',
                to: '/teorilektioner',
                icon: <FileText size={20} />,
              },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="bg-gray-50 border border-gray-200 rounded-3xl p-5 flex items-center gap-4 hover:bg-white hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center text-gray-800 shrink-0 group-hover:text-red-600 transition-colors">
                  {c.icon}
                </div>

                <div className="flex-1">
                  <div className="font-bold text-gray-950 text-sm mb-0.5">
                    {c.name}
                  </div>
                  <div className="text-xs text-gray-400">{c.note}</div>
                </div>

                <div className="font-display font-black text-xl text-gray-950">
                  {c.pris} kr
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="section-tag mb-3">Varför Alfa?</div>

              <h2 className="font-display font-black text-4xl md:text-5xl text-gray-950 uppercase tracking-tight leading-none mb-4">
                Din lokala trafikskola i Kista
              </h2>

              <p className="text-gray-500 mb-8 leading-relaxed max-w-xl">
                Alfa Trafikskola AB på Nordkapsgatan 8 i Kista. Vi kombinerar
                personlig service, moderna arbetssätt och tydlig utbildning.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  [Award, 'Certifierad', 'Trygg utbildning'],
                  [Clock, 'Flexibla tider', 'Vardagar & helger'],
                  [Users, 'Personlig', 'Anpassad plan'],
                  [MapPin, 'Kista', 'Nordkapsgatan 8 Tr'],
                ].map(([Icon, t, d]) => (
                  <div
                    key={t}
                    className="bg-white border border-gray-200 rounded-2xl p-4 flex gap-3"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-800 shrink-0">
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="text-gray-950 font-semibold text-sm">
                        {t}
                      </div>
                      <div className="text-gray-400 text-xs">{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a href="tel:+46737547405" className="btn-red">
                  <Phone size={14} /> Ring Abubakar
                </a>
                <a href="tel:+46704815743" className="btn-red-outline">
                  <Phone size={14} /> Ring Ahmed
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                ['01', 'Boka Online', 'Välj paket och tid direkt.'],
                ['02', 'Körlektion', 'Träna med tydlig plan.'],
                ['03', 'Klara teorin', 'Öva med teoriprov online.'],
                ['04', 'Ta körkortet', 'Vi förbereder dig hela vägen.'],
              ].map(([n, t, d]) => (
                <div
                  key={n}
                  className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="font-display font-black text-4xl text-red-500 mb-3">
                    {n}
                  </div>
                  <div className="font-bold text-gray-950 text-sm mb-1">
                    {t}
                  </div>
                  <div className="text-gray-500 text-xs leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="section-tag mb-3">Recensioner</div>
            <h2 className="font-display font-black text-4xl text-gray-950 uppercase">
              Vad eleverna säger
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                n: 'Sara L.',
                t: 'Fantastisk trafikskola i Kista. Pedagogisk, tydlig och trygg utbildning.',
                s: 5,
              },
              {
                n: 'Ahmed K.',
                t: 'Proffsig service och flexibla tider. Teoriprovövningarna hjälpte mycket.',
                s: 5,
              },
              {
                n: 'Emma J.',
                t: 'Riskutbildningen var välorganiserad och körlektionerna var väldigt lärorika.',
                s: 5,
              },
            ].map((r, i) => (
              <div
                key={i}
                className="bg-gray-50 border border-gray-200 rounded-3xl p-6"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array(r.s)
                    .fill(0)
                    .map((_, j) => (
                      <Star
                        key={j}
                        size={14}
                        className="text-yellow-400 fill-yellow-400"
                      />
                    ))}
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-5 italic">
                  "{r.t}"
                </p>

                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-900 text-xs font-bold">
                    {r.n[0]}
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {r.n}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="section-tag mb-3">FAQ</div>
              <h2 className="font-display font-black text-4xl text-gray-950 uppercase mb-3">
                Vanliga Frågor
              </h2>
              <p className="text-gray-500 mb-6">
                Hittar du inte svaret? Ring oss direkt.
              </p>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <a href="tel:+46737547405" className="btn-red inline-flex">
                  <Phone size={14} /> Abubakar: 073-754 74 05
                </a>
                <a
                  href="tel:+46704815743"
                  className="btn-red-outline inline-flex"
                >
                  <Phone size={14} /> Ahmed: 070-481 57 43
                </a>
              </div>
            </div>

            <div className="space-y-3">
              {FAQ.map((f, i) => (
                <FAQItem key={i} {...f} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gray-50 border border-gray-200 rounded-[2rem] p-8 md:p-10 flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="font-display font-black text-4xl md:text-5xl text-gray-950 uppercase tracking-tight">
                Redo att börja köra?
              </h2>
              <p className="text-gray-500 mt-2">
                Boka hos Alfa Trafikskola i Kista – Nordkapsgatan 8.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/korlektioner" className="btn-red">
                <Car size={15} /> Boka Lektion Nu
              </Link>

              <Link to="/teoriprov" className="btn-red-outline">
                <FileText size={15} /> Teoriprov Gratis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}